import { useState } from 'react';
import { AlertTriangle, Check, Loader2, X, Pencil, Plus } from 'lucide-react';
import { useConfirmAction, useCancelAction } from '../api/assistant';
import type { ChatAction } from '../types/chat';

/**
 * Tarjeta de preview de una acción de escritura (INSERT/UPDATE) propuesta
 * por el asistente. Muestra los campos a tocar y dos botones: Confirmar /
 * Cancelar. Sólo se ejecuta tras confirmación humana.
 */
export function ActionPreviewCard({ action }: { action: ChatAction }) {
  const [localStatus, setLocalStatus] = useState(action.status);
  const [feedback, setFeedback] = useState<string | null>(null);
  const confirm = useConfirmAction();
  const cancel = useCancelAction();

  const isPending = localStatus === 'pending';
  const isInsert = action.operation === 'insert';

  const handleConfirm = async () => {
    try {
      const res = await confirm.mutateAsync(action.id);
      setLocalStatus('confirmed');
      setFeedback(
        isInsert
          ? `✓ Registro creado correctamente.`
          : `✓ Actualizados ${res.affected_rows} registro(s).`,
      );
    } catch (e: any) {
      setFeedback('✕ ' + (e?.response?.data?.message ?? 'Error al ejecutar la acción.'));
    }
  };

  const handleCancel = async () => {
    try {
      await cancel.mutateAsync(action.id);
      setLocalStatus('cancelled');
      setFeedback('Acción cancelada.');
    } catch (e: any) {
      setFeedback('✕ ' + (e?.response?.data?.message ?? 'Error al cancelar.'));
    }
  };

  const opLabel = isInsert ? 'Crear nuevo registro' : 'Modificar registro';

  return (
    <div className={`mt-3 rounded-2xl border-2 overflow-hidden shadow-sm ${
      localStatus === 'confirmed' ? 'border-emerald-300 bg-emerald-50/40' :
      localStatus === 'cancelled' ? 'border-slate-200 bg-slate-50 opacity-60' :
      localStatus === 'failed' ? 'border-red-300 bg-red-50/40' :
      isInsert ? 'border-emerald-200 bg-white' : 'border-amber-200 bg-white'
    }`}>
      {/* Header */}
      <div className={`flex items-center gap-2 px-4 py-2.5 border-b ${
        isInsert ? 'border-emerald-100 bg-emerald-50' : 'border-amber-100 bg-amber-50'
      }`}>
        {isInsert ? (
          <Plus size={16} className="text-emerald-700" />
        ) : (
          <Pencil size={16} className="text-amber-700" />
        )}
        <span className={`text-xs font-black uppercase tracking-widest ${
          isInsert ? 'text-emerald-800' : 'text-amber-800'
        }`}>
          {opLabel}
        </span>
        <span className="text-xs text-slate-500">·</span>
        <code className="text-xs font-bold text-slate-700">{action.target_table}</code>
      </div>

      {/* Body: campos */}
      <div className="px-4 py-3 space-y-1.5">
        {Object.entries(action.preview.columns).map(([key, value]) => (
          <div key={key} className="flex items-baseline gap-3 text-sm">
            <span className="font-bold text-slate-600 min-w-[120px]">{key}:</span>
            <span className="text-slate-800 break-words">{formatValue(value)}</span>
          </div>
        ))}
        {action.preview.where && (
          <div className="mt-2 pt-2 border-t border-slate-100 flex items-baseline gap-3 text-xs">
            <span className="font-bold text-slate-500 min-w-[120px]">WHERE:</span>
            <code className="text-slate-600">{action.preview.where}</code>
          </div>
        )}
      </div>

      {/* Footer: acciones */}
      {isPending && (
        <div className="flex items-center justify-end gap-2 bg-slate-50 px-3 py-2 border-t border-slate-100">
          <button
            onClick={handleCancel}
            disabled={cancel.isPending || confirm.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-200 disabled:opacity-50 transition"
          >
            <X size={14} />
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={cancel.isPending || confirm.isPending}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-black text-white shadow-sm transition disabled:opacity-60 ${
              isInsert
                ? 'bg-emerald-700 hover:bg-emerald-800'
                : 'bg-amber-600 hover:bg-amber-700'
            }`}
          >
            {confirm.isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Check size={14} />
            )}
            Confirmar
          </button>
        </div>
      )}

      {/* Feedback tras acción */}
      {!isPending && feedback && (
        <div className={`px-4 py-2 text-xs flex items-center gap-2 border-t ${
          localStatus === 'confirmed'
            ? 'border-emerald-100 bg-emerald-50 text-emerald-800'
            : localStatus === 'failed'
              ? 'border-red-100 bg-red-50 text-red-800'
              : 'border-slate-100 bg-slate-50 text-slate-600'
        }`}>
          {localStatus === 'failed' && <AlertTriangle size={12} />}
          <span className="font-bold">{feedback}</span>
        </div>
      )}

      {/* Estado terminal sin feedback (mensajes históricos) */}
      {!isPending && !feedback && (
        <div className="px-4 py-2 text-xs bg-slate-50 text-slate-500 border-t border-slate-100">
          {localStatus === 'confirmed' && '✓ Acción ya ejecutada'}
          {localStatus === 'cancelled' && 'Acción cancelada'}
          {localStatus === 'failed' && '✕ La acción falló'}
          {localStatus === 'expired' && 'Preview caducado'}
        </div>
      )}
    </div>
  );
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '(vacío)';
  if (typeof v === 'boolean') return v ? 'sí' : 'no';
  if (typeof v === 'number') return v.toLocaleString('es-ES');
  return String(v);
}
